import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Layers, Loader2, Store, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Markdown } from "@/components/Bits";
import { RootsLine } from "@/components/Roots";
import { IMG } from "@/components/SiteChrome";
import { RootsFoundationsForm } from "@/components/RootsFoundationsForm";

const Shell = ({ step, children }) => (
  <div className="relative z-10 min-h-screen grid lg:grid-cols-5 bg-background">
    <aside className="relative hidden lg:block lg:col-span-2 bg-navy overflow-hidden">
      <img src={IMG.market} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
      <div className="relative p-12 text-white h-full flex flex-col justify-between">
        <img src="/brand/logo.png" alt="Roots Marketing Co" className="h-12 w-auto self-start rounded-lg bg-white p-2" />
        <div><p className="font-serif uppercase text-4xl leading-tight">Strong roots first.<br />Then we grow.</p><RootsLine className="w-64 h-10 text-sun/50 mt-4" /></div>
        <ol className="space-y-2 text-sm">{["Your path", "Roots Growth Scorecard", "Your brand", "Your foundations"].map((s, i) => <li key={s} className={i === step ? "text-sun" : "text-white/50"}>0{i + 1} · {s}</li>)}</ol>
      </div>
    </aside>
    <main className="lg:col-span-3 px-6 sm:px-12 py-12 lg:py-20 max-w-2xl w-full mx-auto">{children}</main>
  </div>
);

const Opt = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} className={`w-full text-left rounded-xl border-2 px-4 py-3 text-sm transition-colors ${active ? "border-pinkb bg-pinkb/5 text-navy" : "border-border hover:border-navy/40"}`}>{children}</button>
);

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState(0);
  const [qi, setQi] = useState(0);
  const [f, setF] = useState({ audience: "", answers: {}, business_name: user?.business_name || "", name: user?.name || "", sells: "", different: "", channel: "", working_on: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { api.get("/onboarding/questions").then((r) => setMeta(r.data)); }, []);
  const writing = result?.status === "generating";
  useEffect(() => {
    if (!writing) return undefined;
    let n = 0;
    const t = setInterval(() => {
      n += 1;
      api.get("/me/foundations").then((r) => {
        if (r.data.status !== "generating" || n > 40) setResult((x) => ({ ...x, foundations: r.data.foundations, status: "ready" }));
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [writing]);
  if (!meta) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-pinkb" /></div>;
  const qs = [...meta.scorecard.slice(0, 1), ...meta.scorecard.slice(5, 6), ...meta.branch.filter((b) => f.audience === "both" || b.aud === f.audience), ...meta.scorecard.slice(1, 5), ...meta.scorecard.slice(6)];
  const q = qs[qi];
  const pick = (i) => {
    setF({ ...f, answers: { ...f.answers, [q.id]: i } });
    setTimeout(() => (qi < qs.length - 1 ? setQi(qi + 1) : setStep(2)), 180);
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding", f);
      setResult(data);
      setStep(3);
      setUser({ ...user, name: f.name || user.name, onboarded: true, audience: f.audience, growth_phase: data.growth_phase, business_name: f.business_name });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const field = (k, label, ph, area) => (
    <label className="block text-sm font-medium text-navy">{label}
      {area ? <textarea required={k !== "working_on"} rows={3} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} data-testid={`onb-${k}`} className="mt-1 w-full rounded-lg border border-input bg-white px-3 py-2 text-sm font-normal" />
        : <input required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} data-testid={`onb-${k}`} className="mt-1 w-full h-11 rounded-lg border border-input bg-white px-3 text-sm font-normal" />}
    </label>
  );
  return (
    <Shell step={step}>
      {step === 0 && (
        <div data-testid="onb-step-audience">
          <div className="eyebrow mb-3">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
          <h1 className="font-serif uppercase text-4xl text-navy leading-tight">I am a…</h1>
          <p className="text-muted-foreground mt-3">This shapes every answer, template and seasonal idea you'll see.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {[["cpg", Store, "CPG food or drink founder", "A food or drink brand going into retail, wholesale, supermarkets or the on-trade"], ["hospitality", UtensilsCrossed, "Hospitality operator", "A restaurant, café, bar or street food business"], ["both", Layers, "Both", "A brand and a venue, so you'll see everything"]].map(([k, Icon, t, d]) => (
              <button key={k} onClick={() => { const ids = meta.branch.map((b) => b.id); setF(k === f.audience ? f : { ...f, audience: k, channel: "", answers: Object.fromEntries(Object.entries(f.answers).filter(([id]) => !ids.includes(id))) }); setStep(1); }} data-testid={`onb-audience-${k}`} className="text-left rounded-2xl border-2 border-border hover:border-pinkb p-6 transition-colors">
                <Icon className="h-8 w-8 text-pinkb" /><div className="font-serif uppercase text-xl text-navy mt-3">{t}</div><p className="text-sm text-muted-foreground mt-1">{d}</p>
              </button>
            ))}
          </div>
        </div>
      )}
      {step === 1 && (
        <div data-testid="onb-step-scorecard">
          <div className="eyebrow mb-3">Roots Growth Scorecard · {qi + 1} of {qs.length}</div>
          <div className="h-1.5 rounded-full bg-secondary mb-8"><div className="h-full rounded-full bg-leaf transition-all" style={{ width: `${((qi + 1) * 100) / qs.length}%` }} /></div>
          <h2 className="font-serif uppercase text-3xl text-navy leading-tight" data-testid="onb-question">{q.q}</h2>
          <div className="mt-6 space-y-2">{q.o.map((o, i) => <Opt key={o} active={f.answers[q.id] === i} onClick={() => pick(i)} testId={`onb-opt-${i}`}>{o}</Opt>)}</div>
          <button onClick={() => (qi ? setQi(qi - 1) : setStep(0))} data-testid="onb-back" className="mt-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"><ArrowLeft className="h-4 w-4" />Back</button>
        </div>
      )}
      {step === 2 && (
        <form onSubmit={submit} className="space-y-5" data-testid="onb-step-profile">
          <div className="eyebrow">Your brand</div>
          <h2 className="font-serif uppercase text-3xl text-navy">Tell me about your business.</h2>
          {field("name", "Your name", "e.g. Sam Smith")}
          {field("business_name", "Business or brand name", "e.g. Fizz & Root Kombucha")}
          {field("sells", "What do you sell, and to whom?", "e.g. Small-batch kombucha to delis and cafes in Bristol", true)}
          {field("different", "What makes you different?", "e.g. Brewed with foraged botanicals, genuinely low sugar", true)}
          <label className="block text-sm font-medium text-navy">Primary sales channel
            <select required value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} data-testid="onb-channel" className="mt-1 w-full h-11 rounded-lg border border-input bg-white px-3 text-sm font-normal">
              <option value="">Choose one</option>{meta.channels[f.audience].map((c) => <option key={c}>{c}</option>)}
            </select></label>
          {field("working_on", "Anything specific you're working on right now? (optional)", "e.g. Launching a new product, getting a retail listing, opening a second site", true)}
          <div className="flex items-center justify-between pt-2">
            <button type="button" onClick={() => { setStep(1); setQi(qs.length - 1); }} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"><ArrowLeft className="h-4 w-4" />Back</button>
            <button type="submit" disabled={busy} data-testid="onb-submit" className="inline-flex items-center gap-2 rounded-full bg-pinkb hover:bg-pinkb/90 text-white px-6 py-3 font-medium disabled:opacity-60">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" />Building your foundations…</> : <>Build my foundations <ArrowRight className="h-4 w-4" /></>}</button>
          </div>
        </form>
      )}
      {step === 3 && result && (
        <div data-testid="onb-step-result">
          <div className="rounded-2xl bg-navy text-white p-6 mb-8">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-sun">Your Roots Growth Phase</div>
            <div className="font-serif uppercase text-4xl mt-2" data-testid="onb-phase">Phase {result.growth_phase}: {result.phase_name}</div>
            <p className="text-white/85 mt-2 text-sm">{result.phase_summary}</p>
          </div>
          <p className="font-serif text-xl text-navy mb-4">Here are your foundations. Everything The Roots Club recommends will be built on these.</p>
          <div className="rounded-2xl border border-border bg-card p-6">
            {writing ? <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="onb-foundations-writing"><Loader2 className="h-4 w-4 animate-spin text-pinkb" />Writing your foundations now. This takes about 20 seconds…</p>
              : <Markdown testId="onb-foundations">{result.foundations}</Markdown>}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button onClick={() => { setStep(4); window.scrollTo({ top: 0 }); }} data-testid="onb-next-foundations" className="inline-flex items-center gap-2 rounded-full bg-pinkb hover:bg-pinkb/90 text-white px-7 py-3 font-medium">Next: your marketing foundations <ArrowRight className="h-4 w-4" /></button>
            <button onClick={() => nav("/app")} data-testid="onb-finish" className="text-sm text-muted-foreground hover:text-navy underline-offset-4 hover:underline">Skip for now, go to my dashboard</button>
          </div>
        </div>
      )}
      {step === 4 && (
        <div data-testid="onb-step-foundations">
          <div className="eyebrow mb-2">Last step · about 15 minutes</div>
          <h1 className="font-serif uppercase text-4xl text-navy leading-[0.95]">Your marketing foundations.</h1>
          <p className="mt-3 mb-6 text-muted-foreground">Four short questions that turn your roots into a filter for every marketing decision. Each answer saves when you press save, so you can stop at any point and finish later from My Roots Profile.</p>
          <RootsFoundationsForm audience={result?.audience || f.audience} />
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button onClick={() => nav("/app")} data-testid="onb-foundations-done" className="inline-flex items-center gap-2 rounded-full bg-pinkb hover:bg-pinkb/90 text-white px-7 py-3 font-medium">Go to my dashboard <ArrowRight className="h-4 w-4" /></button>
            <button onClick={() => nav("/app")} data-testid="onb-foundations-later" className="text-sm text-muted-foreground hover:text-navy hover:underline">I'll finish this later</button>
          </div>
        </div>
      )}
    </Shell>
  );
}
